// Shared handling for welcome email sends and the answers the API gives about them.
//
// Why this exists: a welcome email to an address the mail system had already
// rejected used to come back as a plain success, so the screen said "sent" for
// mail that never left and admins clicked again and again. The API now answers
// honestly (422 with a plain English reason when a person cannot be emailed,
// 429 or 422 when the same person was emailed moments ago, and lists of people
// a bulk send skipped). Every screen that sends a welcome email reads those
// answers through here so the wording is the same everywhere.
//
// A 429 is not always that answer. Both APIs also run a request limit on every
// endpoint (Apiato throttle, 30 a minute per client by default) which answers
// 429 "Too Many Attempts." before the request reaches the welcome email code,
// so nothing was queued at all. Only an answer that carries its own sentence,
// or reason_code "recently_sent", is treated as "emailed a moment ago".
//
// Everything here must also work against the API as it was before that change:
// every field is optional and a missing one falls back to the old behaviour.

export const WELCOME_QUEUED_MESSAGE =
  'Welcome email queued. It usually arrives within a few minutes.';

export const WELCOME_FAILED_MESSAGE =
  'The welcome email could not be sent. Please try again in a few minutes.';

export const WELCOME_TOO_SOON_MESSAGE =
  'A welcome email was sent to this person a moment ago. Please wait a couple of minutes before sending another, as each new email replaces the link in the one before.';

// The same answer for a send to several people at once.
export const BULK_TOO_SOON_MESSAGE =
  'These welcome emails were sent a moment ago. Please wait a couple of minutes before sending them again, as each new email replaces the link in the one before.';

// The request limit answered, not the welcome email code: nothing was queued.
export const TOO_MANY_REQUESTS_MESSAGE =
  'Too many requests just now, so nothing was sent. Please wait a minute and try again.';

// reason_code the API puts on its own "emailed a moment ago" answer.
export const RECENTLY_SENT_REASON_CODE = 'recently_sent';

// Most people named in one bulk warning; the rest are counted, not listed.
const SKIPPED_NAMES_SHOWN = 10;

// Laravel's own wording for a validation or throttle failure. Neither tells an
// admin anything, so they are treated as if the API had given no reason.
const GENERIC_SERVER_MESSAGES = ['The given data was invalid.', 'Too Many Attempts.'];

/**
 * Options for a toast that stays until the admin closes it. Used for anything
 * the admin has to act on: a success toast vanishes in two seconds, which is
 * fine for good news and useless for "this person was not emailed".
 * A fresh object each call so no toast can alter another's settings.
 */
export function persistentToast() {
  return {
    timeout: 0,
    closeOnClick: true,
    // main.js caps toast text at 180 characters for every toast; a skipped
    // list naming several people is longer than that and must not be cut off.
    bodyMaxLength: 2000,
  };
}

function cleanString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * The plain English reason the API gave for refusing a welcome email, or '' if
 * it gave none. Only 422 and 429 carry reasons written for admins; any other
 * status (a 500, a 404, a dropped connection) is ours to word.
 */
export function welcomeEmailServerReason(err) {
  const response = err && err.response;
  if (!response) {
    return '';
  }
  const status = response.status;
  if (status !== 422 && status !== 429) {
    return '';
  }
  const data = response.data;
  if (!data || typeof data !== 'object') {
    return '';
  }
  // a validation style 422 puts the useful sentence under errors.<field>
  if (data.errors && typeof data.errors === 'object') {
    const keys = Object.keys(data.errors);
    for (let i = 0; i < keys.length; i++) {
      // a code for the screen to read, not a sentence for the admin
      if (keys[i] === 'reason_code') {
        continue;
      }
      const value = data.errors[keys[i]];
      const first = cleanString(Array.isArray(value) ? value[0] : value);
      if (first) {
        return first;
      }
    }
  }
  const message = cleanString(data.message);
  if (message && GENERIC_SERVER_MESSAGES.indexOf(message) === -1) {
    return message;
  }
  return '';
}

/**
 * The machine readable reason_code on a 422 or 429 answer, or '' when there is
 * none. Read from the body itself or from inside the Apiato "errors" object.
 */
export function welcomeEmailReasonCode(err) {
  const response = err && err.response;
  if (!response || (response.status !== 422 && response.status !== 429)) {
    return '';
  }
  const data = response.data;
  if (!data || typeof data !== 'object') {
    return '';
  }
  const direct = cleanString(data.reason_code);
  if (direct) {
    return direct;
  }
  if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
    const nested = data.errors.reason_code;
    return cleanString(Array.isArray(nested) ? nested[0] : nested);
  }
  return '';
}

/**
 * What to tell the admin when a welcome email request fails: the API's own
 * reason when it gave one, otherwise a plain message that fits the failure.
 *
 * options.bulk: the request was a send to several people, so the wording must
 * not talk about "this person".
 *
 * A 429 with no reason of its own came from the request limit, not from the
 * welcome email code, so it must not claim an email went out a moment ago.
 */
export function welcomeEmailErrorMessage(err, fallback, options) {
  const bulk = !!(options && options.bulk);
  const reason = welcomeEmailServerReason(err);
  if (reason) {
    return reason;
  }
  if (welcomeEmailReasonCode(err) === RECENTLY_SENT_REASON_CODE) {
    return bulk ? BULK_TOO_SOON_MESSAGE : WELCOME_TOO_SOON_MESSAGE;
  }
  if (err && err.response && err.response.status === 429) {
    return TOO_MANY_REQUESTS_MESSAGE;
  }
  return fallback || WELCOME_FAILED_MESSAGE;
}

/**
 * True when a user row says their welcome email has gone out. Accepts the
 * integer, boolean or string forms the column has been served as.
 */
export function isMailSent(value) {
  return value === true || value === 1 || value === '1';
}

/**
 * The reset_email value for PUT /users/{id}.
 *
 * Both APIs (UpdateUserAction) write is_mail_sent = reset_email ? 0 : 1 on
 * EVERY save, and a missing reset_email counts as false, so it becomes 1.
 * The screens used to send user.is_mail_sent itself, which inverted the flag
 * on every save: a user never emailed was marked as sent (dropping out of "new
 * or outstanding" sends and hiding the send icon), and a user already emailed
 * was marked as not sent.
 *
 * A form whose "Reset welcome email" switch was changed passes the switch
 * state as user.reset_email (on = mark as not sent). Anything else gets the
 * value that leaves is_mail_sent as it is (see freshMailFlagNeeded). If
 * neither is known the field is left out, which is what those callers sent
 * before.
 *
 * Callers: updateUser (admin users Edit, super admin users Edit,
 * PartnerCustomerEdit), updateOwner (Hub partner users Edit and companies
 * Edit), updateOwnerProfile (own Profile page). Every PUT /users/{id} sender.
 */
export function resetEmailFor(user) {
  if (!user) {
    return undefined;
  }
  if (user.reset_email !== undefined && user.reset_email !== null) {
    return user.reset_email ? 1 : 0;
  }
  if (user.is_mail_sent === undefined || user.is_mail_sent === null) {
    return undefined;
  }
  return isMailSent(user.is_mail_sent) ? 0 : 1;
}

/**
 * True when a save should re-read is_mail_sent from the API before sending.
 *
 * Hub sets is_mail_sent = 1 from the queue worker once a welcome email has
 * gone, which can be after an edit form loaded the user. A form holding the
 * stale 0 would send reset_email 1 and put the flag back to 0, so the person
 * is offered (and bulk option 2 sends) a second email that replaces the link
 * in the first. Only a loaded "not sent" can be stale this way, and only when
 * the switch was not changed on purpose.
 */
export function freshMailFlagNeeded(user) {
  if (!user) {
    return false;
  }
  if (user.reset_email !== undefined && user.reset_email !== null) {
    return false;
  }
  if (user.is_mail_sent === undefined || user.is_mail_sent === null) {
    return false;
  }
  return !isMailSent(user.is_mail_sent);
}

/**
 * The user to save, with is_mail_sent taken from a fresh GET /users/{id}
 * response when it has one. Never changes the caller's object.
 */
export function withFreshMailFlag(user, resp) {
  const body = resp && resp.data;
  const fresh = body && body.data && typeof body.data === 'object' ? body.data.is_mail_sent : undefined;
  if (fresh === undefined || fresh === null) {
    return user;
  }
  return Object.assign({}, user, { is_mail_sent: fresh });
}

function normaliseSkipped(entry) {
  if (typeof entry === 'string') {
    const text = entry.trim();
    return text ? { name: text, email: '', reason: '' } : null;
  }
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const person = {
    name: cleanString(entry.name),
    email: cleanString(entry.email),
    reason: cleanString(entry.reason),
  };
  return person.name || person.email ? person : null;
}

function hasBulkFields(candidate) {
  return (
    candidate &&
    typeof candidate === 'object' &&
    !Array.isArray(candidate) &&
    (candidate.queued !== undefined || candidate.skipped !== undefined)
  );
}

/**
 * Reads { queued, skipped } from a bulk welcome email response. The fields may
 * sit on the body itself or inside the Apiato "data" wrapper, or be absent
 * (the API before this change answered with neither). queued is null when the
 * API did not say; skipped is always an array of { name, email, reason }.
 */
export function readBulkWelcomeResult(resp) {
  const result = { queued: null, skipped: [] };
  const body = resp && resp.data;
  if (!body || typeof body !== 'object') {
    return result;
  }
  let source = null;
  if (hasBulkFields(body)) {
    source = body;
  } else if (hasBulkFields(body.data)) {
    source = body.data;
  }
  if (!source) {
    return result;
  }
  if (source.queued !== undefined && source.queued !== null && source.queued !== '') {
    const queued = Number(source.queued);
    result.queued = isNaN(queued) ? null : queued;
  }
  if (Array.isArray(source.skipped)) {
    source.skipped.forEach((entry) => {
      const person = normaliseSkipped(entry);
      if (person) {
        result.skipped.push(person);
      }
    });
  }
  return result;
}

/**
 * The success line for a bulk send. Says how many were queued when the API
 * reported it, otherwise the wording it always used.
 */
export function bulkQueuedMessage(queued) {
  if (queued === null || queued === undefined) {
    return 'Your welcome emails have been queued and will be sent shortly.';
  }
  if (queued === 1) {
    return 'Welcome email queued for 1 person. It usually arrives within a few minutes.';
  }
  return 'Welcome emails queued for ' + queued + ' people. They usually arrive within a few minutes.';
}

/**
 * The warning naming everyone a bulk send left out, with the reason for each.
 * Returns '' when nobody was skipped.
 * e.g. "2 people were not emailed: Tony Nuttman (reason), Robyn Bardella (reason)."
 */
export function describeSkipped(skipped) {
  if (!Array.isArray(skipped) || !skipped.length) {
    return '';
  }
  const shown = skipped.slice(0, SKIPPED_NAMES_SHOWN).map((person) => {
    const label = person.name || person.email;
    return person.reason ? label + ' (' + person.reason + ')' : label;
  });
  const count = skipped.length;
  let text =
    (count === 1 ? '1 person was' : count + ' people were') +
    ' not emailed: ' +
    shown.join(', ');
  const hidden = count - shown.length;
  if (hidden > 0) {
    text += ', and ' + hidden + ' more';
  }
  return text + '.';
}

/**
 * A warning the API attached to a create user response, for example when the
 * new person's address cannot be emailed. Looked for at meta.email_warning and
 * meta.custom.email_warning; '' when there is none.
 */
export function readEmailWarning(resp) {
  const body = resp && resp.data;
  const meta = body && typeof body === 'object' ? body.meta : null;
  if (!meta || typeof meta !== 'object') {
    return '';
  }
  const direct = cleanString(meta.email_warning);
  if (direct) {
    return direct;
  }
  if (meta.custom && typeof meta.custom === 'object') {
    return cleanString(meta.custom.email_warning);
  }
  return '';
}
